/* eslint-disable @typescript-eslint/naming-convention */
import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { Player } from 'fl-player-interface';
import muxjs from 'mux.js';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataService } from 'src/app/data.service';
import { PlayerState } from '../../enums/player-states';
import { IPlayerMeta } from '../../interfaces/player.interface';
import { PlaybackService } from '../../services/playback.service';
import {
  PlayerEvent,
  PlayerEventType,
  SimplePlayerService,
} from './simple-player.service';
import { NrPlaybackSessionService } from 'src/app/analytics/newrelic/services/newrelic.player.service';

@Component({
  selector: 'app-simple-player',
  templateUrl: './simple-player.component.html',
  styleUrls: ['./simple-player.component.scss'],
})
export class SimplePlayerComponent implements OnDestroy, OnInit {
  @ViewChild('videoPlayer') videoPlayer: ElementRef<HTMLVideoElement>;
  @ViewChild('image') image: ElementRef<HTMLImageElement>;

  // Public properties
  showImage = false;
  shouldAnimate = false;

  // Configuration properties
  private readonly VIDEO_LOAD_DELAY = 1500; // 1.5 seconds

  // Private properties
  private _isVerticalMenuOpen = false;
  private _meta: IPlayerMeta;
  private _playerInstance: Player;
  private _isInitialized = false;
  private _isDialogOpen = false;
  private _isVideoCompleted = false; // Flag to prevent multiple completion events

  private playerDebounce: any; // Changed from NodeJS.Timeout to any for browser compatibility
  private pauseTimeout: any; // Timeout for player pause operations
  private destroy$ = new Subject<void>();
  private playerBindFunctions = {
    onError: this.onPlaybackError.bind(this),
    onProgress: this.onProgressUpdate.bind(this),
    onBufferingStateChanged: this.onBufferingStateChanged.bind(this),
  };

  // get imageUrl(): string {
  //   return this._meta?.imageUrl;
  // }

  constructor(
    private readonly playbackService: PlaybackService,
    @Inject(PLATFORM_ID) private platformId: unknown,
    private simplePlayerService: SimplePlayerService,
    private dataService: DataService,
    private nrPlaybackSessionService: NrPlaybackSessionService
  ) {
    this.initializeMuxJS();
  }

  // ===== Lifecycle Methods =====
  ngOnInit(): void {
    //Anything to do here?
  }

  ngAfterViewInit(): void {
    this.initializeSubscriptions();
    this._isInitialized = true;

    // Replay the last event after initialization
    const lastEvent = this.simplePlayerService.getLastEvent();
    if (lastEvent) {
      this.handleEvent(lastEvent);
    }
  }

  ngOnDestroy(): void {
    if (this.playerDebounce) {
      clearTimeout(this.playerDebounce);
      this.playerDebounce = null;
    }
    if (this.pauseTimeout) {
      clearTimeout(this.pauseTimeout);
      this.pauseTimeout = null;
    }
    this.cleanup();
  }

  // ===== Public Methods =====
  updateImageUrl(
    url: string,
    newWidth: number,
    newAspectRatio: string
  ): string {
    return url
      .replace(/width=\d+/, `width=${newWidth}`)
      .replace(/(\d+-)?\d+x\d+\.jpg/, (match) =>
        match.replace(/\d+x\d+/, newAspectRatio)
      );
  }

  // ===== Private Methods - Initialization =====
  private initializeMuxJS(): void {
    try {
      if (isPlatformBrowser(this.platformId)) {
        window.muxjs = muxjs;
      }
    } catch (error) {
      console.error('[SMPL] oninit catch', error);
    }
  }

  private initializeSubscriptions(): void {
    this.simplePlayerService.playerEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (!this._isInitialized) {
          return;
        }
        this.handleEvent(event);
      });

    this.dataService.verticalMenuEvent
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event.action === 'open') {
          this._isVerticalMenuOpen = true;
          if (this._playerInstance) {
            this._playerInstance.pause();
          }
        } else if (event.action === 'close') {
          this._isVerticalMenuOpen = false;
          if (this._playerInstance) {
            this._playerInstance.play();
          }
        }
      });

    this.simplePlayerService.dialogStateChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        // Clear any existing timeout to prevent stale operations
        if (this.pauseTimeout) {
          clearTimeout(this.pauseTimeout);
          this.pauseTimeout = null;
        }

        this._isDialogOpen = state;
        if (!this._isDialogOpen && !this._isVerticalMenuOpen) {
          if (this._playerInstance) {
            this._playerInstance.play();
          }
        } else {
          if (this._playerInstance) {
            this.pauseTimeout = setTimeout(() => {
              this._playerInstance.pause();
              this.pauseTimeout = null;
            }, 500);
          }
        }
      });
  }

  private async initialize(): Promise<boolean> {
    try {
      this._playerInstance = await this.simplePlayerService.initializePlayer(
        this.videoPlayer.nativeElement
      );
      this.nrPlaybackSessionService.initialize(
        this.dataService.currentResourceValue,
        undefined,
        this.dataService.currentResourceValue.apUrl?.dash,
        true
      );
      this.nrPlaybackSessionService.attachPlayer(
        this._playerInstance,
        false,
        this.dataService.currentResourceValue.id
      );
      this.subscribeAll(this._playerInstance);
      return true;
    } catch (error) {
      this.stop();
      console.error('[SMPL] Error on create player', error);
      return false;
    }
  }

  // ===== Private Methods - Player State Management =====
  private async updatePlayerState(states: PlayerState): Promise<void> {
    switch (states) {
      case PlayerState.PLAY:
        const isInitialized = await this.initialize();
        if (isInitialized && !this._isVerticalMenuOpen && !this._isDialogOpen) {
          this._playerInstance.load();
          this._playerInstance.play();
        } else if (!isInitialized) {
          console.error('[SMPL] Player initialization failed');
        }
        break;
      case PlayerState.RESUME:
        if (this._playerInstance) {
          this._playerInstance.play();
        }
        break;
      case PlayerState.PAUSE:
        if (this._playerInstance) {
          this._playerInstance.pause();
        }
        break;
      case PlayerState.STOP:
        this.stop();
        break;
      default:
        console.error(`[SMPL] Unsupported player action: ${states}`);
        this.stop();
    }
  }

  private async stop(): Promise<void> {
    this.setShowImage(true);
    this._isVideoCompleted = false; // Reset completion flag
    if (this._playerInstance) {
      this._playerInstance.pause();
      this._playerInstance.stop();
      this.unsubscribeAll();
      this.nrPlaybackSessionService.detachPlayer();
      this._playerInstance = null;
    }
  }

  private cleanup(): void {
    this.stop();
    // Unsubscribe from all player events
    if (this._playerInstance) {
      this.unsubscribeAll();
      this._playerInstance = null;
    }

    // Complete destroy subject
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Private Methods - UI State Management =====
  private async handleUI(meta: IPlayerMeta): Promise<void> {
    if (!this._isInitialized) {
      return;
    }

    const imageUrl = this.updateImageUrl(meta.imageUrl, 1920, '16x9');
    this._meta = { ...meta, imageUrl };

    // Stop player and show image container first for instant switching
    this.stop();

    // Then set new image source immediately
    this.shouldAnimate = false;
    this.image.nativeElement.src = this._meta?.imageUrl;

    if (meta.isPlayable) {
      this.playbackService.startTime = new Date().getTime();
      if (this.playerDebounce) {
        clearTimeout(this.playerDebounce);
        this.playerDebounce = null;
      }
      this.playerDebounce = setTimeout(async () => {
        await this.updatePlayerState(PlayerState.PLAY);
      }, this.VIDEO_LOAD_DELAY);
    }
  }

  private setShowImage(shouldShow: boolean): void {
    this.showImage = shouldShow;
  }

  private async onPlaybackError(error: any): Promise<void> {
    console.error('[SMPL] Playback error', error);
    this.stop();
    this.setShowImage(true);
  }

  private onBufferingStateChanged(stateResult: string): void {
    // Buffering state changed handler
  }

  private onProgressUpdate(): void {
    try {
      if (!this._playerInstance) {
        return;
      }

      const { currentTime, duration } = this._playerInstance;

      // Validate time values
      if (
        !this.isValidTimeValue(currentTime) ||
        !this.isValidTimeValue(duration)
      ) {
        return;
      }

      if (duration > 0) {
        // Handle video completion - prevent multiple triggers
        if (currentTime < duration) {
          if (this._playerInstance.playerState !== PlayerState.PAUSED) {
            this.setShowImage(false);
          }
          // Reset completion flag when video is playing normally
          this._isVideoCompleted = false;
        } else if (!this._isVideoCompleted) {
          // Only trigger completion once
          this._isVideoCompleted = true;
          this.handleVideoCompletion();
        }
      }
    } catch (error) {
      console.error('[SMPL] Error in progress update:', error);
    }
  }

  private isValidTimeValue(time: number | undefined): time is number {
    return typeof time === 'number' && !isNaN(time);
  }

  private handleVideoCompletion(): void {
    this.setShowImage(true);
    this.simplePlayerService.notifyVideoCompleted();
  }

  // ===== Private Methods - Player Event Management =====
  private subscribeAll(playerInstance: Player): void {
    playerInstance?.subscribe('error', this.playerBindFunctions.onError);
    playerInstance?.subscribe(
      'progressupdate',
      this.playerBindFunctions.onProgress
    );
    playerInstance?.subscribe(
      'bufferingstatechanged',
      this.playerBindFunctions.onBufferingStateChanged
    );
  }

  private unsubscribeAll(): void {
    this._playerInstance?.unsubscribe(
      'error',
      this.playerBindFunctions.onError
    );
    this._playerInstance?.unsubscribe(
      'progressupdate',
      this.playerBindFunctions.onProgress
    );
    this._playerInstance?.unsubscribe(
      'bufferingstatechanged',
      this.playerBindFunctions.onBufferingStateChanged
    );
  }

  private async handleEvent(event: PlayerEvent): Promise<void> {
    switch (event.type) {
      case PlayerEventType.META:
        if (event.data) {
          this.image.nativeElement.src = '';
          this.handleUI(event.data as IPlayerMeta);
        } else {
          this.resetAllCachedProperties();
        }
        break;
      case PlayerEventType.STATE:
        this.updatePlayerState(event.data as PlayerState);
        break;
      case PlayerEventType.FORCE_DESTROY:
        await this.hardDestroy();
        break;
      case PlayerEventType.FORCE_RESTORE:
        if (this.videoPlayer?.nativeElement) {
          this.videoPlayer.nativeElement.style.display = 'block';
        }
        break;
    }
  }

  private resetAllCachedProperties(): void {
    this._meta = null;

    if (this.playerDebounce) {
      clearTimeout(this.playerDebounce);
      this.playerDebounce = null;
    }

    this.stop();

    // Force clear the image to prevent showing cached old image
    if (this.image?.nativeElement) {
      this.image.nativeElement.src = '';
      this.shouldAnimate = false;
    }

    this.setShowImage(false);
  }

  onImageLoad(): void {
    // Trigger animation when image loads
    this.shouldAnimate = true;
  }

  onImageError(event: any): void {
    this.shouldAnimate = false;
    this.setShowImage(false);
  }

  private async hardDestroy(): Promise<void> {
    try {
      this.stop();
      this._playerInstance = null;
    } catch (err) {
      console.warn('[SMPL] Error destroying player instance:', err);
    }

    if (this.videoPlayer?.nativeElement) {
      try {
        const video = this.videoPlayer.nativeElement;
        video.pause();
        video.removeAttribute('src');
        video.src = '';
        (video as any).srcObject = null;
        video.load();

        // Hide the video tag completely (prevents ghost image)
        video.style.display = 'none';

        // Force layout reflow to ensure visibility is updated
        void video.offsetHeight;
      } catch (e) {
        console.warn('[SMPL] Error cleaning up video element:', e);
      }
    }
  }
}
